import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
// FormControl e ReactiveFormsModule são essenciais aqui
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Observable, of, BehaviorSubject, combineLatest } from 'rxjs';
import { startWith, switchMap, debounceTime, catchError, map, tap, distinctUntilChanged } from 'rxjs/operators';
import { SelectPesquisavelService } from './select-pesquisavel.service';

@Component({
  selector: 'app-select-pesquisavel',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
  templateUrl: './select-pesquisavel.component.html',
  styleUrls: ['./select-pesquisavel.component.css'],
  // REMOVEMOS O PROVIDER NG_VALUE_ACCESSOR
})
export class SelectPesquisavelComponent<T extends { [key: string]: any }> implements OnInit, OnChanges {

  // ENTRADAS (INPUTS)
  @Input({ required: true }) control: FormControl; // Recebe o FormControl diretamente
  @Input() label: string = 'Selecione';
  @Input() idField: keyof T;
  @Input() nameField: keyof T;
  @Input() secondaryField: keyof T | null = null;
  @Input() apiUrl: string | null = null;
  @Input() offlineList: T[] = [];
  @Input() preloadedOptions: T[] = [];
  @Input() isOptional: boolean = false
  @Input() multiple: boolean = false;
  @Input() errorMessage: string = 'Este campo é obrigatório.';

  // SAÍDAS (OUTPUTS)
  @Output() selectionChange = new EventEmitter<any>();
  @Output() searchChange = new EventEmitter<string>();

  // PROPRIEDADES INTERNAS
  public searchControl = new FormControl('');
  public filteredOptions$: Observable<T[]>;
  private preloadedOptions$ = new BehaviorSubject<T[]>([]);
  private selectedOptions$ = new BehaviorSubject<T[]>([]);
  private optionsCache = new Map<any, T>();


  // MUDANÇA 2: Criar um BehaviorSubject para a URL da API. Isso a torna "observável".
  private apiUrl$ = new BehaviorSubject<string | null>(null);

  // MUDANÇA 3: Criar um BehaviorSubject para offlineList
  private offlineList$ = new BehaviorSubject<T[]>([]);

  constructor(private selectService: SelectPesquisavelService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preloadedOptions'] && this.preloadedOptions) {
      this.preloadedOptions.forEach(opt => this.optionsCache.set(opt[this.idField], opt));
      this.preloadedOptions$.next(this.preloadedOptions);
      this.updateCurrentSelectionFromCache();
    }
    if (changes['apiUrl']) {
      this.apiUrl$.next(this.apiUrl);
    }
    if (changes['offlineList']) {
      this.offlineList$.next(this.offlineList);
    }
  }

  ngOnInit(): void {
    if (!this.idField || !this.nameField) {
      throw new Error('As propriedades idField e nameField são obrigatórias.');
    }
    if (!this.control) {
      throw new Error('A propriedade [control] é obrigatória.');
    }

    // Ouve o valor do controle principal para garantir que as opções selecionadas
    // sejam mantidas na lista visível.
    this.control.valueChanges.pipe(startWith(this.control.value)).subscribe(() => {
      this.updateCurrentSelectionFromCache();
    });

    const debouncedSearch$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      tap(value => this.searchChange.emit(value || ''))
    );

    this.filteredOptions$ = combineLatest([debouncedSearch$, this.preloadedOptions$, this.selectedOptions$, this.apiUrl$.pipe(distinctUntilChanged()), this.offlineList$]).pipe(
      // A lógica de busca e combinação de opções permanece a mesma
      switchMap(([searchText, preloaded, selectedItems, apiUrl, offlineList]) => {
        const source$ = apiUrl
          ? this.selectService.getSelectValues<T>(apiUrl, searchText || '').pipe(
            catchError(() => of([])),
            map(options => {
              if (!searchText) return options;

              const searchLower = searchText.toLowerCase();
              return options.filter(option => {
                const nameMatch = String(option[this.nameField]).toLowerCase().includes(searchLower);

                // Busca também por CNPJ se disponível
                let cnpjMatch = false;
                if (this.secondaryField === 'cnpjFormatado' && option['cnpjLimpo']) {
                  cnpjMatch = option['cnpjLimpo'].includes(searchText.replace(/[.\-\/\s]/g, ''));
                } else if (this.secondaryField && option[this.secondaryField]) {
                  cnpjMatch = String(option[this.secondaryField]).toLowerCase().includes(searchLower);
                }

                return nameMatch || cnpjMatch;
              });
            })
          )
          : of(offlineList.filter(option => {
            const searchLower = (searchText || '').toLowerCase();
            const nameMatch = String(option[this.nameField]).toLowerCase().includes(searchLower);

            // Busca também por CNPJ se disponível
            let cnpjMatch = false;
            if (this.secondaryField === 'cnpjFormatado' && option['cnpjLimpo']) {
              cnpjMatch = option['cnpjLimpo'].includes(searchText?.replace(/[.\-\/\s]/g, '') || '');
            } else if (this.secondaryField && option[this.secondaryField]) {
              cnpjMatch = String(option[this.secondaryField]).toLowerCase().includes(searchLower);
            }

            return nameMatch || cnpjMatch;
          }));

        return source$.pipe(
          tap(options => {
            options.forEach(opt => this.optionsCache.set(opt[this.idField], opt));
          }),
          map(options => {
            const combined = new Map<any, T>();
            if (selectedItems) {
              selectedItems.forEach(item => combined.set(item[this.idField], item));
            }
            if (preloaded) {
              preloaded.forEach(opt => combined.set(opt[this.idField], opt));
            }
            options.forEach(opt => combined.set(opt[this.idField], opt));
            return Array.from(combined.values());
          })
        );
      })
    );
  }

  private updateCurrentSelectionFromCache(): void {
    const selectedIdOrIds = this.control.value;
    if (!selectedIdOrIds) {
      this.selectedOptions$.next([]);
      return;
    }

    if (this.multiple && Array.isArray(selectedIdOrIds)) {
      const selectedObjects = selectedIdOrIds
        .map(id => this.optionsCache.get(id))
        .filter((item): item is T => !!item);
      this.selectedOptions$.next(selectedObjects);
    } else if (!this.multiple) {
      const selectedObject = this.optionsCache.get(selectedIdOrIds);
      this.selectedOptions$.next(selectedObject ? [selectedObject] : []);
    }
  }
}